import { Component, OnInit, EventEmitter, Output } from '@angular/core';
import { HttpEventType } from '@angular/common/http';
import { DomSanitizer } from '@angular/platform-browser';

import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { switchMap, map } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';

import { SettingsService, RegistrationService, BcModalService } from '../../services';
import { DocumentsService } from '../../../natural-person/shared/documents.service';
import { Document } from '../../../natural-person/shared/document.model';


@Component({
  selector: 'bc-documents-upload',
  templateUrl: './documents-upload.component.html',
  styleUrls: ['./documents-upload.component.scss']
})
export class DocumentsUploadComponent implements OnInit {
  contacts: ContactDetails;
  @Output() removed = new EventEmitter();

  documents: any;
  progress$: Observable<any>;
  documentsExist$ = new BehaviorSubject(false);
  firstParty: string;
  allowedMimeTypesToUpload: string;

  constructor(
    public registrationService: RegistrationService,
    private domSanitizer: DomSanitizer,
    private toastr: ToastrService,
    private bcModalService: BcModalService,
    public documentsService: DocumentsService,
    private settingsService: SettingsService,
    ) { }

  ngOnInit() {
    this.firstParty = this.registrationService.root.parties[0]._id;
    this.getDocuments();

    this.allowedMimeTypesToUpload = this.settingsService.getAllowedFileTypesForAccept();
  }

  getDocuments() {
    this.documentsService.getPartyDocuments(this.registrationService.root._id, this.firstParty)
      .pipe(
        map((documents: any) => documents.filter(document => document.type === 'O'))
      )
      .subscribe((documents) => {
        this.documents = documents;
        this.documentsExist$.next(this.documents.some(({type}) => type === 'O'));
      });
  }

  uploadFile(event: any) {
    const file = event.value.filter((file: File) => this.allowedMimeTypesToUpload.indexOf(file.type) >= 0)[0];
    if (!file) return;

    this.progress$ = this.documentsService.preUploadUrl(file.name.normalize(), file.type, this.firstParty)
      .pipe(
        switchMap((response: {_id: string, signedUploadUrl: string}) => {
          return combineLatest(of(response), this.documentsService.uploadFileWithProgressReport(response.signedUploadUrl, file));
        }),
        switchMap((responses: any) => {
          const [response, progress] = responses;

          if (progress.type !== HttpEventType.Response) return of(responses);

          const fileName = file.name.normalize();
          const document = {
            _id: response._id,
            partyId: this.firstParty,
            name: fileName,
            type: 'O',
            fileName,
          };

          return this.documentsService.postUpload(<Document> document);
        }),
        map((postUpload) => {
          if (!this.isProgress(postUpload)) {
            this.documents.push(postUpload);
            this.getDocuments();

            this.documentsExist$.next(true);

            this.toastr.success($localize `:@@uploadSuccess:Your file has been uploaded successfully`);

            return null;
          }

          return postUpload;
        })
      );
  }

  documentRemoved(id: string) {
    this.documentsExist$.next(false);
  }

  viewDocument(id: string) {
    this.documentsService.getDocument(id)
      .subscribe(({uri}) => window.open(uri, '_blank'));
  }

  removeDocument(id: string) {
    const questions = [$localize `:@@uploadRemoveConfirmation:This action will delete the file permanently. Are you sure?`];
    const confirmationModal = this.bcModalService.openConfirmationBox(questions);

    confirmationModal.instance.close.subscribe(() => confirmationModal.destroy());
    confirmationModal.instance.yes.subscribe(() => {
      this.documentsService.removeDocument(id)
      .subscribe(() => {
        const index = this.documents.findIndex((document) => document._id === id);

        if (index !== -1) {
          this.documents.splice(index, 1);
        }

        const indexRoot = this.documents.findIndex((document) => document._id === id);

        if (indexRoot !== -1) {
          this.documents.splice(indexRoot, 1);
        }

        if (this.documents.length === 0) {
          this.removed.emit(id);
        }

        confirmationModal.destroy();
        this.toastr.success($localize `:@@uploadRemoveSuccess:Your file has been removed successfully`);
      });
    });
    confirmationModal.instance.no.subscribe(() => {
      confirmationModal.destroy();
    });

  }

  mapDocumentsToView(document) {
    return {
      ...document,
      fileType: /(?:\.([^.]+))?$/.exec(document.fileName)[1].toLowerCase(),
      url: document.uri,
      uri: this.domSanitizer.bypassSecurityTrustUrl(document.uri)
    };
  }

  private isProgress(event) {
    // eslint-disable-next-line no-prototype-builtins
    return !event.hasOwnProperty('_id');
  }

}
